import type {
  TargetAndTransition,
  MotionProps,
  Variant,
} from 'framer-motion';

export const DEFAULT_HIDDEN: TargetAndTransition = {
  opacity: 0,
  x: 0,
  y: 0,
};

export const DEFAULT_VISIBLE: TargetAndTransition = {
  opacity: 1,
  transition: {
    duration: 1,
    ease: 'easeInOut',
  },
  x: 0,
  y: 0,
};

// Utility to merge variants
export function buildVariant(
  base: TargetAndTransition,
  override: TargetAndTransition = {},
): TargetAndTransition {
  return {
    ...base,
    transition: {
      ...base.transition,
      ...(override.transition ?? {}),
    },
    ...override,
  };
}

// Hook factory for animations
function useMotionAnimation(
  motionPropsOverride: MotionProps = {},
  hiddenOverride: TargetAndTransition = {},
  visibleOverride: TargetAndTransition = {},
): MotionProps {
  return {
    initial: 'hidden',
    animate: 'visible',
    variants: {
      hidden: buildVariant(DEFAULT_HIDDEN, hiddenOverride),
      visible: buildVariant(DEFAULT_VISIBLE, visibleOverride),
    },
    ...motionPropsOverride,
  };
}

export function buildHiddenVariant(variant: TargetAndTransition = {}): Variant {
  return {
    ...DEFAULT_HIDDEN,
    transition: {
      ...DEFAULT_HIDDEN.transition,
      ...(variant.transition ?? {}),
    },
    ...variant,
  };
}

export function buildVisibleVariant(
  variant: TargetAndTransition = {},
): Variant {
  return {
    ...DEFAULT_VISIBLE,
    transition: {
      ...DEFAULT_VISIBLE.transition,
      ...(variant.transition ?? {}),
    },
    ...variant,
  };
}

export function useFadeIn(
  motionPropsOverride: MotionProps = {},
  hiddenOverride: TargetAndTransition = {},
  visibleOverride: TargetAndTransition = {},
): MotionProps {
  return useMotionAnimation(
    motionPropsOverride,
    hiddenOverride,
    visibleOverride,
  );
}

export function useStaggerFadeIn(
  staggerChildren = 0.3,
  motionPropsOverride: MotionProps = {},
  hiddenOverride: TargetAndTransition = {},
  visibleOverride: TargetAndTransition = {},
): MotionProps {
  return useMotionAnimation(motionPropsOverride, hiddenOverride, {
    ...visibleOverride,
    transition: { ...(visibleOverride.transition ?? {}), staggerChildren },
  });
}

export function useFadeInBottom(
  value = 20,
  motionPropsOverride: MotionProps = {},
  hiddenVariantOverride: TargetAndTransition = {},
  visibleVariantOverride: TargetAndTransition = {},
): MotionProps {
  return useFadeIn(
    motionPropsOverride,
    { ...hiddenVariantOverride, y: value },
    visibleVariantOverride,
  );
}

export function useFadeInRight(
  value = 50,
  motionPropsOverride: MotionProps = {},
  hiddenVariantOverride: TargetAndTransition = {},
  visibleVariantOverride: TargetAndTransition = {},
): MotionProps {
  return useFadeIn(
    motionPropsOverride,
    { ...hiddenVariantOverride, x: value },
    visibleVariantOverride,
  );
}
